import { Loader2 } from "lucide-react";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import logoXero from "@/assets/logo-xero.png";
import {
  useGetXeroStatus,
  usePushInvoiceToXero,
  usePushEstimateToXero,
  useGetXeroInvoiceSyncStatus,
  useGetXeroEstimateSyncStatus,
} from "@workspace/api-client-react";

type Props =
  | { kind: "invoice"; id: number; testId?: string }
  | { kind: "estimate"; id: number; testId?: string };

function extractError(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e?.response?.data?.error || e?.message || "Unknown error";
}

/**
 * Row-level "Push to Xero" dropdown item that mirrors the detail-page
 * XeroPushButton behavior: looks up per-record sync status to flip the label
 * between "Push" and "Update", uses matching toast wording, shows a pending
 * spinner, and respects needsReconnect.
 *
 * Only render this when the Xero connection is established (caller checks
 * `xeroPush.connected` from `useXeroPush`).
 */
export function XeroPushMenuItem(props: Props) {
  const { toast } = useToast();
  const { data: status } = useGetXeroStatus();

  const isInvoice = props.kind === "invoice";
  const connected = status?.connected === true;
  const needsReconnect =
    (status as { needsReconnect?: boolean } | undefined)?.needsReconnect === true;

  const pushInvoice = usePushInvoiceToXero();
  const pushEstimate = usePushEstimateToXero();

  const invoiceSync = useGetXeroInvoiceSyncStatus(isInvoice ? props.id : undefined, {
    enabled: isInvoice && connected,
  });
  const estimateSync = useGetXeroEstimateSyncStatus(!isInvoice ? props.id : undefined, {
    enabled: !isInvoice && connected,
  });
  const syncData = isInvoice ? invoiceSync.data : estimateSync.data;
  const hasPushed = !!syncData?.externalId || !!syncData?.lastSuccess;
  const isPending = isInvoice ? pushInvoice.isPending : pushEstimate.isPending;

  const label = hasPushed ? "Update in Xero" : "Push to Xero";

  const onClick = async (e: Event) => {
    e.preventDefault();
    try {
      if (isInvoice) await pushInvoice.mutateAsync(props.id);
      else await pushEstimate.mutateAsync(props.id);
      toast({
        title: hasPushed ? "Updated in Xero" : "Pushed to Xero",
        description: isInvoice ? "Invoice synced." : "Estimate synced.",
      });
    } catch (err) {
      toast({
        title: "Xero push failed",
        description: extractError(err),
        variant: "destructive",
      });
    }
  };

  return (
    <DropdownMenuItem
      disabled={isPending || needsReconnect}
      onSelect={onClick}
      data-testid={props.testId ?? `menu-xero-push-${props.kind}-${props.id}`}
      title={needsReconnect ? "Reconnect Xero in Integrations to push." : undefined}
    >
      {isPending ? (
        <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
      ) : (
        <img src={logoXero} alt="Xero" className="w-3.5 h-3.5 mr-2 object-contain" />
      )}
      {label}
    </DropdownMenuItem>
  );
}
